import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type {
  ShopifyDiscountCampaign,
  ShopifyStoreDiscount,
  ShopifyStoreDiscountStatus,
} from '@discovr/types';
import { shopifyDiscountsCopy as copy } from '@discovr/features/copy';
import { ArrowSquareOut, MagnifyingGlass } from '@phosphor-icons/react';
import { QueryErrorState } from '../components/common/QueryErrorState';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { EmptyState } from '../components/common/EmptyState';
import { DataTable, StickyTableToolbar, type ColumnDef } from '../components/common/DataTable';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SimpleTooltip } from '../components/ui/tooltip';
import { TruncatedText } from '../components/common/TruncatedText';
import { PhaseSubTabs } from '../campaign-detail/components/parts/PhaseSubTabs';
import { cn } from '../lib/utils';
import { ShopifyCodeCell } from '../campaign-detail/components/shopifyCouponCells';
import { fmtStoreDate } from '../campaign-detail/components/shopifyStoreTime';
import { shopifyDiscountUrl } from '../campaign-detail/components/shopifyOrdersUrl';
import {
  discountsView,
  hasActiveDiscountFilters,
  type ShopifyDiscountsSearch,
  type ShopifyDiscountsView,
} from './shopifyDiscountsSearch';
import { formatDiscountTypeValue } from './shopifyDiscountValue';

export interface ShopifyStoreDiscountsProps {
  items: ShopifyStoreDiscount[];
  filters: ShopifyDiscountsSearch;
  onFiltersChange: (patch: Partial<ShopifyDiscountsSearch>) => void;
  onClearFilters: () => void;
  /** Switching tab keeps status and search and drops the other tab's filters. */
  onViewChange: (view: ShopifyDiscountsView) => void;
  /** Options of the Discovr tab's campaign filter, in the order to show them. */
  campaigns?: ShopifyDiscountCampaign[];
  /** Store domain (`x.myshopify.com`); row links to Shopify are omitted when it is unusable. */
  shopDomain?: string | null;
  /** The store's IANA zone; dates show in UTC when it is unknown. */
  storeTimezone?: string;
  loading?: boolean;
  /** Showing the previous results while a changed filter reloads. */
  refreshing?: boolean;
  error?: boolean;
  onRetry?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Wraps the campaign name in the app's router link; keeps this component router-free. */
  renderCampaignLink: (campaignId: string, className: string, children: ReactNode) => ReactNode;
}

const SHOWN_CODES = 2;
const SEARCH_DEBOUNCE_MS = 300;
const ALL = 'all';

type StatusTab = 'all' | NonNullable<ShopifyDiscountsSearch['status']>;

const intFmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

const STATUS_VARIANT: Record<ShopifyStoreDiscountStatus, 'default' | 'secondary' | 'outline'> = {
  ACTIVE: 'default',
  SCHEDULED: 'secondary',
  EXPIRED: 'outline',
};

const fmtDate = (iso: string | null, tz?: string): string | null =>
  iso ? fmtStoreDate(iso, tz) || null : null;

function CodesCell({ discount }: { discount: ShopifyStoreDiscount }) {
  const shown = discount.codes.slice(0, SHOWN_CODES);
  if (shown.length === 0) {
    return (
      <span className="text-muted-foreground">
        {discount.method === 'automatic' ? copy.automatic : copy.noValue}
      </span>
    );
  }
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      {shown.map((code) => (
        <ShopifyCodeCell key={code} code={code} />
      ))}
      {discount.codes_total > shown.length ? (
        <span className="text-muted-foreground text-caption-regular">
          {copy.moreCodes(shown.length, discount.codes_total)}
        </span>
      ) : null}
    </span>
  );
}

function SelectFilter({
  label,
  allLabel,
  value,
  options,
  onChange,
  className = 'min-w-40',
}: {
  label: string;
  allLabel: string;
  value: string | undefined;
  options: Record<string, string>;
  onChange: (value: string | undefined) => void;
  className?: string;
}) {
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
      <SelectTrigger size="sm" aria-label={label} className={className}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {Object.entries(options).map(([key, text]) => (
          <SelectItem key={key} value={key}>
            {text}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Search box state: debounced into the URL, and reset together with Clear filters. */
function useSearchDraft(
  q: string | undefined,
  onFiltersChange: ShopifyStoreDiscountsProps['onFiltersChange'],
) {
  const [draft, setDraft] = useState(q ?? '');
  const emitted = useRef(q ?? '');

  // Follow the URL only when it moved without us (back/forward).
  useEffect(() => {
    const external = q ?? '';
    if (external === emitted.current) return;
    emitted.current = external;
    setDraft(external);
  }, [q]);

  useEffect(() => {
    const next = draft.trim();
    if (next === emitted.current) return;
    const timer = setTimeout(() => {
      emitted.current = next;
      onFiltersChange({ q: next || undefined });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, onFiltersChange]);

  const reset = () => {
    emitted.current = '';
    setDraft('');
  };
  return { draft, setDraft, reset };
}

function DiscountsToolbar({
  filters,
  onFiltersChange,
  campaigns,
  onClear,
  draft,
  onDraftChange,
  count,
}: Pick<ShopifyStoreDiscountsProps, 'filters' | 'onFiltersChange' | 'campaigns'> & {
  onClear: () => void;
  draft: string;
  onDraftChange: (value: string) => void;
  count: string | null;
}) {
  const f = copy.filters;
  const view = discountsView(filters);
  const campaignOptions = Object.fromEntries(
    (campaigns ?? []).map((c) => [c.campaign_id, c.campaign_name || copy.untitledCampaign]),
  );
  const statusTabs: { key: StatusTab; label: string }[] = [
    { key: ALL, label: f.all },
    ...(Object.entries(f.status) as [NonNullable<ShopifyDiscountsSearch['status']>, string][]).map(
      ([key, label]) => ({ key, label }),
    ),
  ];
  return (
    <StickyTableToolbar>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs min-w-56 flex-1">
          <Label htmlFor="shopify-discounts-search" className="sr-only">
            {f.searchLabel}
          </Label>
          <MagnifyingGlass
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
          />
          <Input
            id="shopify-discounts-search"
            type="search"
            placeholder={view === 'discovr' ? f.searchPlaceholderDiscovr : f.searchPlaceholder}
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            className="pl-8"
          />
        </div>
        <PhaseSubTabs<StatusTab>
          variant="pill"
          bare
          ariaLabel={f.statusTabs}
          active={filters.status ?? ALL}
          tabs={statusTabs}
          onChange={(v) => onFiltersChange({ status: v === ALL ? undefined : v })}
        />
        {view === 'discovr' ? (
          <SelectFilter
            label={f.campaignLabel}
            allLabel={f.allCampaigns}
            value={filters.campaign}
            options={campaignOptions}
            onChange={(v) => onFiltersChange({ campaign: v })}
            className="max-w-xs min-w-40"
          />
        ) : (
          <>
            <SelectFilter
              label={f.typeLabel}
              allLabel={f.allTypes}
              value={filters.type}
              options={f.type}
              onChange={(v) => onFiltersChange({ type: v as ShopifyDiscountsSearch['type'] })}
            />
            <SelectFilter
              label={f.methodLabel}
              allLabel={f.allMethods}
              value={filters.method}
              options={f.method}
              onChange={(v) => onFiltersChange({ method: v as ShopifyDiscountsSearch['method'] })}
            />
          </>
        )}
        {hasActiveDiscountFilters(filters) ? (
          <Button variant="ghost" size="sm" onClick={onClear}>
            {f.clear}
          </Button>
        ) : null}
        {count ? (
          <span
            role="status"
            data-testid="shopify-discounts-count"
            className="text-muted-foreground text-body-sm-regular ml-auto"
          >
            {count}
          </span>
        ) : null}
      </div>
    </StickyTableToolbar>
  );
}

function CreatorCell({ discount }: { discount: ShopifyStoreDiscount }) {
  const name = discount.discovr?.creator_name;
  const handle = discount.discovr?.creator_handle;
  if (!name && !handle) return <span className="text-muted-foreground">{copy.noValue}</span>;
  return (
    <div className="min-w-0">
      {name ? <TruncatedText text={name} className="text-body-sm-medium block" /> : null}
      {handle ? (
        <TruncatedText
          text={`@${handle}`}
          className="text-muted-foreground text-caption-regular block"
        />
      ) : null}
    </div>
  );
}

function buildColumns({
  view,
  shopDomain,
  storeTimezone,
  renderCampaignLink,
}: Pick<ShopifyStoreDiscountsProps, 'shopDomain' | 'storeTimezone' | 'renderCampaignLink'> & {
  view: ShopifyDiscountsView;
}): ColumnDef<ShopifyStoreDiscount>[] {
  const lead: ColumnDef<ShopifyStoreDiscount>[] =
    view === 'discovr'
      ? [
          {
            id: 'code',
            header: copy.columns.code,
            minWidth: 200,
            cell: (d) => <CodesCell discount={d} />,
          },
          {
            id: 'creator',
            header: copy.columns.creator,
            minWidth: 180,
            cell: (d) => <CreatorCell discount={d} />,
          },
          {
            id: 'campaign',
            header: copy.columns.campaign,
            minWidth: 200,
            grow: true,
            cell: (d) =>
              d.discovr ? (
                <span className="flex min-w-0 items-center">
                  {renderCampaignLink(
                    d.discovr.campaign_id,
                    'text-primary text-body-sm-medium truncate underline underline-offset-4',
                    d.discovr.campaign_name || copy.untitledCampaign,
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground">{copy.noValue}</span>
              ),
          },
          {
            id: 'discount',
            header: copy.columns.discount,
            minWidth: 170,
            cell: (d) => (
              <span className="text-body-sm-regular">
                {formatDiscountTypeValue(d) ?? copy.noValue}
              </span>
            ),
          },
        ]
      : [
          {
            id: 'title',
            header: copy.columns.title,
            minWidth: 220,
            grow: true,
            cell: (d) => (
              <span className="text-body-sm-medium text-foreground block truncate" title={d.title}>
                {d.title}
              </span>
            ),
          },
          {
            id: 'codes',
            header: copy.columns.codes,
            minWidth: 200,
            cell: (d) => <CodesCell discount={d} />,
          },
          {
            id: 'type',
            header: copy.columns.type,
            minWidth: 170,
            cell: (d) => (
              <span className="text-body-sm-regular">
                {formatDiscountTypeValue(d) ?? copy.noValue}
              </span>
            ),
          },
        ];
  return [
    ...lead,
    {
      id: 'status',
      header: copy.columns.status,
      minWidth: 90,
      cell: (d) => <Badge variant={STATUS_VARIANT[d.status]}>{copy.status[d.status]}</Badge>,
    },
    {
      id: 'uses',
      header: copy.columns.uses,
      minWidth: 70,
      align: 'right',
      cell: (d) => (d.uses === null ? copy.noValue : intFmt.format(d.uses)),
    },
    {
      id: 'dates',
      header: copy.columns.dates,
      minWidth: 220,
      cell: (d) => (
        <span className="text-muted-foreground">
          {copy.dates(fmtDate(d.starts_at, storeTimezone), fmtDate(d.ends_at, storeTimezone))}
        </span>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{copy.columns.actions}</span>,
      label: copy.columns.actions,
      minWidth: 48,
      align: 'right',
      cell: (d) => {
        const href = shopifyDiscountUrl(shopDomain, d.id);
        if (!href) return null;
        const title = view === 'discovr' ? (d.codes[0] ?? d.title) : d.title;
        return (
          <SimpleTooltip content={copy.viewInShopify}>
            <Button asChild variant="ghost" size="icon-sm">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${copy.viewInShopify}: ${title} ${copy.newTab}`}
              >
                <ArrowSquareOut size={16} />
              </a>
            </Button>
          </SimpleTooltip>
        );
      },
    },
  ];
}

export function ShopifyStoreDiscounts({
  items,
  filters,
  onFiltersChange,
  onClearFilters,
  onViewChange,
  campaigns,
  shopDomain,
  storeTimezone,
  loading,
  refreshing,
  error,
  onRetry,
  hasMore,
  loadingMore,
  onLoadMore,
  renderCampaignLink,
}: ShopifyStoreDiscountsProps) {
  const { draft, setDraft, reset } = useSearchDraft(filters.q, onFiltersChange);
  const clear = () => {
    reset();
    onClearFilters();
  };
  const view = discountsView(filters);
  const columns = useMemo(
    () => buildColumns({ view, shopDomain, storeTimezone, renderCampaignLink }),
    [view, shopDomain, storeTimezone, renderCampaignLink],
  );

  const loadMore = hasMore ? (
    <Button variant="outline" size="sm" loading={loadingMore} onClick={onLoadMore}>
      {loadingMore ? copy.loadingMore : copy.loadMore}
    </Button>
  ) : null;

  // A page can come back empty while more exist, so "no results" is only true at the last page.
  let emptyState: ReactNode;
  if (hasMore) {
    emptyState = (
      <div className="flex flex-col items-center gap-3">
        <p className="text-body-sm-medium text-muted-foreground">{copy.noneLoadedYet}</p>
        {loadMore}
      </div>
    );
  } else if (hasActiveDiscountFilters(filters)) {
    emptyState = (
      <EmptyState
        icon={MagnifyingGlass}
        title={copy.emptyFiltered}
        body={copy.emptyFilteredBody}
        className="py-6"
        action={{ label: copy.filters.clear, onClick: clear }}
      />
    );
  } else if (view === 'discovr') {
    emptyState = <EmptyState title={copy.emptyDiscovr} body={copy.emptyDiscovrBody} className="py-6" />;
  } else {
    emptyState = <EmptyState title={copy.empty} className="py-6" />;
  }

  const showCount = !loading && !error;
  const toolbar = (
    <DiscountsToolbar
      filters={filters}
      onFiltersChange={onFiltersChange}
      campaigns={campaigns}
      onClear={clear}
      draft={draft}
      onDraftChange={setDraft}
      count={showCount ? copy.resultCount(items.length, Boolean(hasMore)) : null}
    />
  );

  const tabs = (
    <PhaseSubTabs<ShopifyDiscountsView>
      variant="underline"
      ariaLabel={copy.views.ariaLabel}
      active={view}
      tabs={[
        { key: 'discovr', label: copy.views.discovr },
        { key: 'other', label: copy.views.other },
      ]}
      onChange={onViewChange}
    />
  );

  if (error) {
    return (
      <div className="flex flex-col gap-5">
        {tabs}
        {toolbar}
        <div role="alert">
          <QueryErrorState message={copy.loadError} onRetry={onRetry} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {tabs}
      {toolbar}
      <div className="space-y-3">
        <div
          data-testid="shopify-discounts-table"
          aria-busy={loading || refreshing ? 'true' : undefined}
          className={cn('transition-opacity', refreshing && 'opacity-60')}
        >
          <DataTable<ShopifyStoreDiscount>
            ariaLabel={copy.views[view]}
            columns={columns}
            data={items}
            getRowId={(d) => d.id}
            loading={loading}
            emptyState={emptyState}
            autoHeight
          />
        </div>
        {items.length > 0 ? loadMore : null}
      </div>
    </div>
  );
}
